import React from "react";
import { EntitySingleSelect } from "@/components/EntitySingleSelect/EntitySingleSelect";
import {
  SECTIONS,
  SectionCode,
} from "@/modules/hub/features/MemberDashboard/config/kpiConfig";
import LoadingIndicator from "@/components/LoadingIndicator/LoadingIndicator";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import SearchInput from "@/components/SearchInput/SearchInput";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { KPIOverviewCoach } from "../../api/fetchKPIOverviewCoaches/types";
import { useKPIOverviewPage } from "../../hooks/useKPIOverviewPage";
import { KPIStatus, STATUS_STYLES } from "../../utils/kpiOverviewStatus";
import { KPI_OVERVIEW_SEGMENTS } from "../../utils/kpiOverviewSegment";
import { KPI_OVERVIEW_SORTS } from "../../utils/kpiOverviewSort";
import { KPI_OVERVIEW_VIEWS } from "../../utils/kpiOverviewView";
import KPIOverviewCompactTile from "../KPIOverviewCompactTile/KPIOverviewCompactTile";
import KPIOverviewTableCell from "../KPIOverviewTableCell/KPIOverviewTableCell";
import KPIOverviewTile from "../KPIOverviewTile/KPIOverviewTile";
import KPIOverviewTradeTags from "../KPIOverviewTradeTags/KPIOverviewTradeTags";

const LEGEND_STATUSES: KPIStatus[] = ["on_target", "watch", "off_target"];

const SECTION_OPTIONS = (Object.keys(SECTIONS) as SectionCode[]).map(
  (code) => ({ code, title: SECTIONS[code].title }),
);

const KPIOverviewPage: React.FC = () => {
  const {
    panels,
    totalCount,
    loading,
    page,
    pageCount,
    hasPreviousPage,
    hasNextPage,
    searchTerm,
    view,
    segment,
    segmentTitle,
    segmentCounts,
    periodLabel,
    coach,
    fetchCoaches,
    visibleSections,
    handleVisibleSectionsChange,
    handleSearchChange,
    handleCoachChange,
    handleViewChange,
    handleSegmentChange,
    sort,
    handleSortChange,
    goToPreviousPage,
    goToNextPage,
  } = useKPIOverviewPage();

  return (
    <MainPageWrapper fullWidth hideHeader title="KPI Overview">
      <header className="mb-4">
        <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
          SOAR · Coach view
        </p>
        <h1 className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
          {segmentTitle}
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {periodLabel}
        </p>
      </header>

      <div className="mb-4 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <ToggleGroup
          aria-label="Segment"
          className="w-fit flex-wrap justify-start rounded-md border border-gray-200 bg-white p-0.5 dark:border-slate-700 dark:bg-slate-800"
          onValueChange={(value) => value && handleSegmentChange(value)}
          size="sm"
          type="single"
          value={segment}
        >
          {KPI_OVERVIEW_SEGMENTS.map((option) => (
            <ToggleGroupItem
              className="group gap-1.5 px-3 font-normal text-gray-500 hover:bg-transparent hover:text-gray-900 data-[state=on]:bg-primary data-[state=on]:text-white dark:text-gray-400 dark:hover:text-white"
              key={option.value}
              value={option.value}
            >
              {option.label}
              <span className="text-xs tabular-nums text-gray-400 group-data-[state=on]:text-white/80 dark:text-gray-500">
                {segmentCounts[option.countKey]}
              </span>
            </ToggleGroupItem>
          ))}
        </ToggleGroup>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <SearchInput
            className="w-full sm:w-80"
            onChange={handleSearchChange}
            placeholder="Search members"
            value={searchTerm}
          />
          <Select onValueChange={handleSortChange} value={sort}>
            <SelectTrigger aria-label="Sort" className="w-full sm:w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {KPI_OVERVIEW_SORTS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div aria-label="Coach" className="w-full sm:w-56" role="group">
            <EntitySingleSelect<KPIOverviewCoach>
              allowEmpty
              entityNamePlural="Coaches"
              entityNameSingular="Coach"
              fetchEntities={fetchCoaches}
              onChange={handleCoachChange}
              placeholder="All coaches"
              value={coach}
            />
          </div>
          <ToggleGroup
            aria-label="View"
            className="w-fit justify-start rounded-md border border-gray-200 bg-white p-0.5 dark:border-slate-700 dark:bg-slate-800"
            onValueChange={(value) => value && handleViewChange(value)}
            size="sm"
            type="single"
            value={view}
          >
            {KPI_OVERVIEW_VIEWS.map((option) => (
              <ToggleGroupItem
                className="px-3 font-normal text-gray-500 hover:bg-transparent hover:text-gray-900 data-[state=on]:bg-primary data-[state=on]:text-white dark:text-gray-400 dark:hover:text-white"
                key={option.value}
                value={option.value}
              >
                {option.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      </div>

      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <ToggleGroup
            aria-label="Show sections"
            className="flex-wrap justify-start gap-1.5"
            onValueChange={handleVisibleSectionsChange}
            size="sm"
            type="multiple"
            value={visibleSections}
          >
            {SECTION_OPTIONS.map((section) => (
              <ToggleGroupItem
                className="h-7 rounded-full border border-gray-200 px-3 text-xs font-normal text-gray-500 hover:bg-transparent hover:text-gray-900 data-[state=on]:border-primary data-[state=on]:bg-primary data-[state=on]:text-white dark:border-slate-700 dark:text-gray-400 dark:hover:text-white"
                key={section.code}
                value={section.code}
              >
                {section.title}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>

        <ul
          aria-label="Status legend"
          className="flex flex-wrap items-center justify-end gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400"
        >
          {LEGEND_STATUSES.map((status) => (
            <li className="flex items-center gap-1.5" key={status}>
              <span
                aria-hidden="true"
                className={`h-2 w-2 rounded-full ${STATUS_STYLES[status].dot}`}
              />
              {STATUS_STYLES[status].label}
            </li>
          ))}
        </ul>
      </div>

      {/* Over the results only, so the toolbar stays usable while they load. */}
      <div aria-busy={loading} className="relative min-h-64">
        {loading && (
          <div className="absolute inset-0 z-10 bg-gray-50/60 dark:bg-slate-950/60">
            <div
              aria-label="Loading companies"
              className="sticky top-1/3 flex justify-center pt-16"
              role="status"
            >
              <LoadingIndicator isFullScreen={false} />
            </div>
          </div>
        )}

        {!loading && panels.length === 0 && (
          <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            No companies match
          </p>
        )}

        {view === "cards" && (
          <div className="flex flex-col gap-4">
            {panels.map((panel) => (
              <section
                aria-label={panel.companyName}
                className={`rounded-lg border border-l-4 border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800 ${STATUS_STYLES[panel.overallStatus].border}`}
                key={panel.uuid}
              >
                <header className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex flex-col gap-1">
                    <h2 className="text-base font-normal text-gray-900 dark:text-white">
                      {panel.companyName}
                    </h2>
                    {panel.location && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {panel.location}
                      </p>
                    )}
                    <KPIOverviewTradeTags trades={panel.trades} />
                  </div>
                  {panel.coachName && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {panel.coachName}
                    </p>
                  )}
                </header>

                <div className="flex flex-col gap-4">
                  {panel.rows.map((row) => (
                    <div key={row.code}>
                      <div className="mb-2 flex items-center justify-between">
                        <h3 className="text-sm font-normal text-gray-900 dark:text-white">
                          {row.title}
                        </h3>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {row.source}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
                        {row.tiles.map((tile) => (
                          <KPIOverviewTile key={tile.code} tile={tile} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        {view === "compact" && (
          <div className="flex flex-col gap-2">
            {panels.map((panel) => (
              <section
                aria-label={panel.companyName}
                className={`overflow-x-auto rounded-lg border border-l-4 border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800 ${STATUS_STYLES[panel.overallStatus].border}`}
                key={panel.uuid}
              >
                <div className="flex min-w-[1200px]">
                  <div className="flex w-72 shrink-0 items-center justify-between gap-3 border-r border-gray-100 p-3 dark:border-slate-700/50">
                    <div className="flex min-w-0 flex-col gap-1">
                      <h2
                        className="truncate text-base text-gray-900 dark:text-white"
                        title={panel.companyName}
                      >
                        {panel.companyName}
                      </h2>
                      {panel.location && (
                        <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                          {panel.location}
                        </p>
                      )}
                      <KPIOverviewTradeTags trades={panel.trades} />
                    </div>
                    {panel.coachName && (
                      <p className="min-w-0 truncate text-right text-xs text-gray-500 dark:text-gray-400">
                        {panel.coachName}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-1 gap-3 p-2">
                    {panel.rows.map((row) => (
                      <div
                        className="flex min-w-0 flex-1 flex-col gap-1"
                        key={row.code}
                      >
                        <div className="flex items-center justify-between px-1">
                          <h3 className="text-xs font-normal text-gray-900 dark:text-white">
                            {row.title}
                          </h3>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {row.source}
                          </span>
                        </div>
                        <div className="grid grid-cols-5 gap-1">
                          {row.tiles.map((tile) => (
                            <KPIOverviewCompactTile
                              key={tile.code}
                              tile={tile}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            ))}
          </div>
        )}

        {view === "table" && panels.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800">
            <Table className="min-w-[1200px]">
              <TableHeader>
                <TableRow className="border-gray-100 dark:border-slate-700/50">
                  <TableHead
                    className="border-r border-gray-100 align-middle text-[11px] font-medium uppercase tracking-wider text-gray-600 dark:border-slate-700/50 dark:text-gray-300"
                    rowSpan={2}
                  >
                    Member
                  </TableHead>
                  {panels[0].rows.map((row) => (
                    <TableHead
                      className="border-l border-gray-100 text-center text-[11px] font-medium uppercase tracking-wider text-gray-700 dark:border-slate-700/50 dark:text-gray-200"
                      colSpan={row.tiles.length}
                      key={row.code}
                    >
                      {row.title}
                    </TableHead>
                  ))}
                </TableRow>
                <TableRow className="border-gray-100 dark:border-slate-700/50">
                  {panels[0].rows.flatMap((row) =>
                    row.tiles.map((tile) => (
                      <TableHead
                        className="whitespace-nowrap text-right text-[11px] font-medium uppercase tracking-wider text-gray-600 dark:text-gray-300"
                        key={tile.code}
                      >
                        {tile.shortLabel}
                      </TableHead>
                    )),
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {panels.map((panel) => (
                  <TableRow
                    className="border-gray-100 dark:border-slate-700/50"
                    key={panel.uuid}
                  >
                    <th
                      className="whitespace-nowrap border-r border-gray-100 px-3 py-2 text-left font-normal dark:border-slate-700/50"
                      scope="row"
                    >
                      <div className="flex flex-col gap-1">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {panel.companyName}
                        </div>
                        <KPIOverviewTradeTags trades={panel.trades} />
                        {panel.coachName && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {panel.coachName}
                          </div>
                        )}
                      </div>
                    </th>
                    {panel.rows.flatMap((row) =>
                      row.tiles.map((tile) => (
                        <KPIOverviewTableCell key={tile.code} tile={tile} />
                      )),
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {totalCount > 0 && (
          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {totalCount} companies, page {page} of {pageCount}
            </p>
            <div className="flex items-center gap-2">
              <Button
                className="rounded-md"
                disabled={!hasPreviousPage}
                onClick={goToPreviousPage}
                size="sm"
                variant="outline"
              >
                Previous
              </Button>
              <Button
                className="rounded-md"
                disabled={!hasNextPage}
                onClick={goToNextPage}
                size="sm"
                variant="outline"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </MainPageWrapper>
  );
};

export default KPIOverviewPage;
